import React, { useCallback, useEffect, useState } from "react";
import {
  Check,
  EyeOff,
  Loader2,
  Mail,
  MessageSquareText,
  ShieldAlert,
  Star,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";

const STATUS_CONFIG = {
  auto_published: {
    label: "Auto-Published",
    className: "border-emerald-200 bg-emerald-50 text-emerald-800",
  },
  manual_published: {
    label: "Published",
    className: "border-emerald-200 bg-emerald-50 text-emerald-800",
  },
  pending: {
    label: "Pending",
    className: "border-amber-200 bg-amber-50 text-amber-800",
  },
  rejected: {
    label: "Rejected",
    className: "border-red-200 bg-red-50 text-red-800",
  },
  unpublished: {
    label: "Unpublished",
    className: "border-slate-200 bg-slate-100 text-slate-700",
  },
};

const formatReviewDate = (value) => {
  if (!value) return "Date unavailable";
  return new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
};

const formatLabel = (value) => String(value || "").replace(/_/g, " ");

export const FeedbackReviewsTab = () => {
  const [filter, setFilter] = useState("all");
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  const [collectionSettings, setCollectionSettings] = useState(null);
  const [savingSetting, setSavingSetting] = useState("");
  const [insights, setInsights] = useState(null);

  const loadReviews = useCallback(async (nextFilter = filter) => {
    setLoading(true);
    try {
      const response = await api.get("/admin/reviews", { params: { status: nextFilter } });
      setReviews(response.data);
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  useEffect(() => {
    api.get("/admin/feedback-collection-settings")
      .then((response) => setCollectionSettings(response.data))
      .catch((error) => toast.error(formatError(error)));
  }, []);

  const loadInsights = useCallback(async () => {
    try {
      const response = await api.get("/admin/reviews/insights");
      setInsights(response.data);
    } catch (error) {
      toast.error(formatError(error));
    }
  }, []);

  useEffect(() => {
    loadInsights();
  }, [loadInsights]);

  const toggleCollectionSetting = async (key) => {
    if (!collectionSettings || savingSetting) return;
    const next = { ...collectionSettings, [key]: !collectionSettings[key] };
    setSavingSetting(key);
    try {
      const response = await api.put("/admin/feedback-collection-settings", next);
      setCollectionSettings(response.data);
      toast.success("Feedback collection settings saved");
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setSavingSetting("");
    }
  };

  const moderateReview = async (review, action) => {
    setActingId(review.id);
    try {
      await api.post(`/admin/reviews/${review.id}/${action}`);
      toast.success(
        action === "approve"
          ? "Review published"
          : action === "reject"
            ? "Review rejected"
            : action === "republish"
              ? "Review republished"
            : "Review unpublished",
      );
      await loadReviews();
      await loadInsights();
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="w-full min-w-0 max-w-6xl overflow-x-hidden" data-testid="feedback-reviews-tab">
      <div className="flex flex-col justify-between gap-4 border-b border-[#E2DCD2] pb-6 sm:flex-row sm:items-end">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#8C6D46]">
            Customer feedback
          </p>
          <h2 className="mt-2 font-serif text-3xl font-semibold text-[#1E2A24]">
            Feedback & Reviews
          </h2>
          <p className="mt-2 text-sm text-[#64748B]">
            Moderate pending customer reviews and manage what appears on product pages.
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="review-moderation-filters">
          {[
            ["all", "All"],
            ["urgent", "Urgent"],
            ["pending", "Pending"],
            ["published", "Published"],
            ["rejected", "Rejected"],
            ["unpublished", "Unpublished"],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setFilter(value)}
              className={`border px-3 py-2 text-xs font-semibold transition-colors ${
                filter === value
                  ? value === "urgent"
                    ? "border-red-700 bg-red-700 text-white"
                    : "border-[#2D5A47] bg-[#2D5A47] text-white"
                  : "border-[#E2DCD2] bg-white text-[#64748B] hover:border-[#2D5A47]"
              }`}
              data-testid={`review-filter-${value}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <section className="mt-6 border border-[#E2DCD2] bg-[#FDFBF7] p-5" data-testid="feedback-collection-settings">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#8C6D46]">Collection controls</p>
        <h3 className="mt-2 font-serif text-2xl font-semibold text-[#1E2A24]">Feedback Collection Settings</h3>
        <p className="mt-2 text-sm text-[#64748B]">Enable one or all customer feedback touchpoints independently.</p>
        {!collectionSettings ? (
          <div className="mt-5 text-sm text-[#64748B]" data-testid="feedback-collection-settings-loading">Loading settings…</div>
        ) : (
          <div className="mt-5 space-y-3">
            {[
              {
                key: "email_feedback_request_enabled",
                title: "Email Feedback Request",
                detail: "Sends the existing feedback email 2 hours after delivery.",
                icon: Mail,
              },
              {
                key: "post_login_popup_enabled",
                title: "Post-Login Popup",
                detail: "Prompts immediately after delivery for unreviewed orders from the last 7 days.",
                icon: MessageSquareText,
              },
              {
                key: "tracking_page_feedback_enabled",
                title: "Order Tracking Page",
                detail: "Shows an inline product feedback form immediately after delivery.",
                icon: Star,
              },
            ].map(({ key, title, detail, icon: Icon }) => {
              const enabled = !!collectionSettings[key];
              const saving = savingSetting === key;
              return (
                <div
                  key={key}
                  className={`flex flex-col gap-3 border p-4 sm:flex-row sm:items-center sm:justify-between ${
                    enabled ? "border-emerald-200 bg-emerald-50" : "border-[#E2DCD2] bg-white"
                  }`}
                  data-testid={`feedback-setting-${key}`}
                >
                  <div className="flex min-w-0 items-start gap-3">
                    <Icon size={18} className={enabled ? "mt-0.5 text-emerald-700" : "mt-0.5 text-[#8C6D46]"} />
                    <div>
                      <div className="font-semibold text-[#1E2A24]">{title}</div>
                      <div className="mt-1 text-xs text-[#64748B]">{detail}</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleCollectionSetting(key)}
                    disabled={savingSetting !== ""}
                    className={`min-w-24 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${
                      enabled ? "bg-emerald-600 hover:bg-emerald-700" : "bg-[#7F1D1D] hover:bg-[#641515]"
                    }`}
                    data-testid={`toggle-feedback-setting-${key}`}
                    aria-pressed={enabled}
                  >
                    {saving ? "Saving…" : enabled ? "Enabled" : "Disabled"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {insights && (
        <section
          className="mt-6 border border-[#D9C3A5] bg-[#FDFBF7] p-5"
          data-testid="review-jev-insights"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#8C6D46]">Jev insights</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="border border-[#E2DCD2] bg-white p-3" data-testid="review-jev-analyzed-count">
              <div className="text-xs text-[#64748B]">Analyzed reviews</div>
              <div className="mt-1 font-serif text-2xl text-[#1E2A24]">{insights.analyzed_count}</div>
            </div>
            <div className="border border-[#E2DCD2] bg-white p-3" data-testid="review-jev-sentiment-summary">
              <div className="text-xs text-[#64748B]">Sentiment</div>
              <div className="mt-1 text-xs text-[#1E2A24]">
                {insights.sentiment.positive} positive · {insights.sentiment.neutral} neutral · {insights.sentiment.negative} negative
              </div>
            </div>
            <div className="border border-[#E2DCD2] bg-white p-3" data-testid="review-jev-topic-summary">
              <div className="text-xs text-[#64748B]">Most discussed</div>
              <div className="mt-1 text-xs capitalize text-[#1E2A24]">
                {Object.entries(insights.topics)
                  .filter(([, count]) => count > 0)
                  .sort(([, first], [, second]) => second - first)
                  .slice(0, 2)
                  .map(([topic]) => formatLabel(topic))
                  .join(" · ") || "No topics yet"}
              </div>
            </div>
            <div className="border border-red-200 bg-red-50 p-3" data-testid="review-jev-urgent-count">
              <div className="text-xs text-red-700">Urgent attention</div>
              <div className="mt-1 font-serif text-2xl text-red-800">{insights.urgent_count}</div>
            </div>
          </div>
        </section>
      )}

      {loading ? (
        <div className="flex min-h-52 items-center justify-center text-[#64748B]" data-testid="reviews-loading">
          <Loader2 className="mr-2 animate-spin" size={18} /> Loading reviews…
        </div>
      ) : reviews.length === 0 ? (
        <div className="py-16 text-center text-sm text-[#64748B]" data-testid="reviews-empty-state">
          No reviews match this filter.
        </div>
      ) : (
        <div className="mt-6 space-y-4" data-testid="reviews-moderation-list">
          {reviews.map((review) => {
            const status = STATUS_CONFIG[review.moderation_status] || STATUS_CONFIG.pending;
            const isActing = actingId === review.id;
            const analysis = review.ai_analysis || {};
            return (
              <article
                key={review.id}
                className="border border-[#E2DCD2] bg-white p-5 shadow-[0_8px_24px_rgba(45,90,71,0.04)]"
                data-testid={`admin-review-${review.id}`}
              >
                <div className="flex flex-col justify-between gap-4 lg:flex-row">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="flex text-[#D99B26]" data-testid={`admin-review-stars-${review.id}`}>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            size={15}
                            fill={star <= review.rating ? "#D99B26" : "none"}
                            strokeWidth={0}
                          />
                        ))}
                      </div>
                      <span className={`border px-2 py-1 text-[10px] font-bold tracking-wide ${status.className}`}>
                        {status.label}
                      </span>
                      {review.moderation_reason === "abusive_language" || (
                        review.moderation_reason || "").includes("abusive_language")
                      ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700">
                          <ShieldAlert size={13} /> Language flag
                        </span>
                      : null}
                      {analysis.sentiment && (
                        <span className="border border-[#D9C3A5] bg-[#FDFBF7] px-2 py-1 text-[10px] font-bold capitalize text-[#8C6D46]">
                          {analysis.sentiment}
                        </span>
                      )}
                      {analysis.urgent && (
                        <span className="inline-flex items-center gap-1 border border-red-200 bg-red-50 px-2 py-1 text-[10px] font-bold text-red-700">
                          <ShieldAlert size={12} /> Urgent
                        </span>
                      )}
                    </div>
                    <h3 className="mt-3 truncate font-semibold text-[#1E2A24]">
                      {review.product_name}
                    </h3>
                    <p className="mt-1 text-xs text-[#64748B]">
                      {review.user_name || "Customer"} · {formatReviewDate(review.created_at)}
                      {review.variant_label ? ` · ${review.variant_label}` : ""}
                    </p>
                    <p className="mt-4 max-w-full break-words whitespace-pre-wrap text-sm leading-6 text-[#4B5563]">
                      {review.comment || "No written review."}
                    </p>
                    {analysis.topics?.length > 0 && (
                      <p className="mt-3 text-xs capitalize text-[#64748B]" data-testid={`review-topics-${review.id}`}>
                        Topics: {analysis.topics.map(formatLabel).join(" · ")}
                      </p>
                    )}
                    {analysis.urgent_issues?.length > 0 && (
                      <p className="mt-2 text-xs font-semibold capitalize text-red-700" data-testid={`review-urgent-issues-${review.id}`}>
                        Needs attention: {analysis.urgent_issues.map(formatLabel).join(" · ")}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 flex-wrap content-start gap-2">
                    {review.moderation_status === "pending" && (
                      <>
                        <button
                          type="button"
                          onClick={() => moderateReview(review, "approve")}
                          disabled={isActing}
                          className="inline-flex items-center gap-1.5 bg-[#2D5A47] px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
                          data-testid={`approve-review-${review.id}`}
                        >
                          <Check size={14} /> Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => moderateReview(review, "reject")}
                          disabled={isActing}
                          className="inline-flex items-center gap-1.5 border border-red-200 px-3 py-2 text-xs font-semibold text-red-700 disabled:opacity-50"
                          data-testid={`reject-review-${review.id}`}
                        >
                          <X size={14} /> Reject
                        </button>
                      </>
                    )}
                    {["auto_published", "manual_published"].includes(review.moderation_status) && (
                      <button
                        type="button"
                        onClick={() => moderateReview(review, "unpublish")}
                        disabled={isActing}
                        className="inline-flex items-center gap-1.5 border border-[#8C6D46] px-3 py-2 text-xs font-semibold text-[#8C6D46] disabled:opacity-50"
                        data-testid={`unpublish-review-${review.id}`}
                      >
                        <EyeOff size={14} /> Unpublish
                      </button>
                    )}
                    {review.moderation_status === "unpublished" && (
                      <button
                        type="button"
                        onClick={() => moderateReview(review, "republish")}
                        disabled={isActing}
                        className="inline-flex items-center gap-1.5 bg-[#2D5A47] px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
                        data-testid={`republish-review-${review.id}`}
                      >
                        <Check size={14} /> Republish
                      </button>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};